import type { WebLedgerHints } from '@/features/browserWallet/ledgerHints';
import type {
    TradeNotificationUpdate,
    TradeStatus,
} from '@/features/wallet/types';
import type { FeedFill } from '@snappy/core/lib/hl/feeds';

// The V2 trade-notification mapping (port of InOut.Mobile `features/wallet/hooks/useTradeNotifications.ts`): the
// wallet's Hyperliquid `userFills`, `orderUpdates` and `clearinghouseState` pushes become the same
// `TradeNotificationUpdate` messages the V1 trade socket sends. React-free and engine-free (type imports only), so it
// is tested on its own and the hook that feeds it stays out of the engine chunk.

/** A fill and the order update of the same TP/SL trigger both report it: the second one within this window is dropped. */
export const DEDUPE_WINDOW_MS = 10_000;

/** Both dexes push their clearinghouse state together: coalesce them into one invalidation. */
export const INVALIDATE_COALESCE_MS = 100;

/**
 * How long a filled trigger order's update waits for its fill, which says whether the position is now flat: a fill
 * that arrives decides, one that never arrives leaves the update to stand on its own (triggers are placed full size).
 */
export const ORDER_UPDATE_GRACE_MS = 3_000;

/** Relative tolerance for "the position is flat" (sizes are decimal strings, so the subtraction can be off by an ulp). */
const FLAT_TOLERANCE = 1e-9;

/** Order statuses that end an order without a fill of their own (`canceled`, `marginCanceled`, `scheduledCancel`, ...). */
const CANCELLED_STATUS = /cancel/i;

export interface V2InvalidateScope {
    /** Coins a fill or an order change touched. */
    coins: string[];
    /** A clearinghouse push changed the account (a fill, a transfer, funding, a margin change). */
    account: boolean;
}

export interface V2NotificationSourceOptions {
    networkId: number;
    readHints: () => WebLedgerHints;
    emit: (update: TradeNotificationUpdate) => void;
    /** Coalesced: the reads that a push made stale. */
    invalidate: (scope: V2InvalidateScope) => void;
    /** The trade of `entryOid` on `coin` closed: its per-position hints are done with. */
    forgetClosed?: (coin: string, entryOid: string) => void;
    now?: () => number;
}

export interface V2NotificationSource {
    fills(fills: FeedFill[]): void;
    orderUpdates(updates: unknown[]): void;
    clearinghouse(state: unknown, dex: string): void;
    /** The feed dropped: the next clearinghouse push invalidates whatever it says. */
    disconnected(): void;
    dispose(): void;
}

interface PositionSnapshot {
    szi?: unknown;
    entryPx?: unknown;
    positionValue?: unknown;
    unrealizedPnl?: unknown;
    liquidationPx?: unknown;
    marginUsed?: unknown;
}

function triggerKind(
    hints: WebLedgerHints,
    coin: string,
    oid: number,
): 'tp' | 'sl' | undefined {
    const recorded = hints.triggerOids[String(oid)];
    if (recorded) {
        return recorded;
    }
    // the per-coin hints describe the current position's triggers only
    if (hints.tpOids[coin] === oid) {
        return 'tp';
    }
    return hints.slOids[coin] === oid ? 'sl' : undefined;
}

function closeStatus(kind: 'tp' | 'sl' | undefined): TradeStatus {
    if (kind === 'tp') {
        return 'TakeProfit';
    }
    return kind === 'sl' ? 'StopLoss' : 'Closed';
}

function isFlat(size: number): boolean {
    return Number.isFinite(size) && Math.abs(size) <= FLAT_TOLERANCE;
}

/** Whether the fill leaves the position flat, so a partial close does not end the trade. Unreadable sizes count as flat. */
function leavesFlat(fill: FeedFill): boolean {
    const before = Math.abs(Number(fill.startPosition));
    const size = Number(fill.sz);
    if (!Number.isFinite(before) || !Number.isFinite(size)) {
        return true;
    }
    return Math.abs(before - size) <= FLAT_TOLERANCE * Math.max(1, before);
}

/** The trade hash of an entry order: `hl:<oid>` for a limit entry (as V1 writes it), the bare oid otherwise. */
function tradeHashFor(hints: WebLedgerHints, entryOid: string): string {
    return hints.limitOrders[entryOid] ? `hl:${entryOid}` : entryOid;
}

interface OrderUpdate {
    coin: string;
    oid: number;
    status: string;
    limitPx: string | null;
    /** No part of the order filled. */
    unfilled: boolean;
}

function orderUpdateOf(update: unknown): OrderUpdate | null {
    if (typeof update !== 'object' || update === null) {
        return null;
    }
    const { order, status } = update as { order?: unknown; status?: unknown };
    if (
        typeof status !== 'string' ||
        typeof order !== 'object' ||
        order === null
    ) {
        return null;
    }
    const { coin, oid, limitPx, sz, origSz } = order as Record<string, unknown>;
    if (typeof coin !== 'string' || typeof oid !== 'number') {
        return null;
    }
    return {
        coin,
        oid,
        status,
        limitPx: typeof limitPx === 'string' ? limitPx : null,
        unfilled:
            typeof sz === 'string' &&
            typeof origSz === 'string' &&
            Number(sz) === Number(origSz),
    };
}

/**
 * The parts of a clearinghouse state that only a fill, a transfer, funding or a margin change moves. Hyperliquid
 * re-pushes the state every few seconds with mark-driven figures that the position polling already covers. Null when
 * the state is unreadable.
 */
function accountFingerprint(state: unknown): string | null {
    if (typeof state !== 'object' || state === null) {
        return null;
    }
    const { marginSummary, assetPositions } = state as {
        marginSummary?: { totalRawUsd?: unknown };
        assetPositions?: unknown;
    };
    if (!Array.isArray(assetPositions)) {
        return null;
    }
    const positions = assetPositions.map((entry: unknown) => {
        const position = (
            entry as { position?: Record<string, unknown> } | null
        )?.position;
        return [
            position?.coin,
            position?.szi,
            position?.entryPx,
            position?.leverage,
        ];
    });
    return JSON.stringify([marginSummary?.totalRawUsd, positions]);
}

function positionsOf(state: unknown): Map<string, PositionSnapshot> {
    const out = new Map<string, PositionSnapshot>();
    const assetPositions = (state as { assetPositions?: unknown } | null)
        ?.assetPositions;
    if (!Array.isArray(assetPositions)) {
        return out;
    }
    for (const entry of assetPositions) {
        const position = (
            entry as { position?: Record<string, unknown> } | null
        )?.position;
        if (position && typeof position.coin === 'string') {
            out.set(position.coin, position);
        }
    }
    return out;
}

function str(value: unknown): string | undefined {
    return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * The mapping:
 * - an entry fill (`dir` starting with `Open`) of a recorded limit entry, or one that opens from flat, is `Open` for
 *   that entry's trade, with the position figures of the latest clearinghouse push when it already shows the coin;
 * - a fill that closes a position (`dir` starting with `Close`, or a liquidation) and leaves it flat is `Liquidated`,
 *   `TakeProfit` / `StopLoss` for a recorded trigger order, else `Closed`, for the coin's recorded entry;
 * - an `orderUpdates` entry reporting a recorded TP/SL trigger as `filled` waits for its fill and maps the same way
 *   only when none arrives within {@link ORDER_UPDATE_GRACE_MS};
 * - a recorded limit entry reported `open` is `Resting` (with its price), reported cancelled with nothing filled is
 *   `Cancelled`;
 * - every push that can change the account invalidates the reads (coalesced).
 * One message per order and outcome within {@link DEDUPE_WINDOW_MS}.
 */
export function createV2TradeNotificationSource(
    options: V2NotificationSourceOptions,
): V2NotificationSource {
    const { networkId, readHints, emit, invalidate, forgetClosed } = options;
    const now = options.now ?? (() => Date.now());
    const emittedAt = new Map<string, number>();
    const filledOids = new Map<number, number>();
    const waitingForFill = new Map<number, ReturnType<typeof setTimeout>>();
    const fingerprints = new Map<string, string | null>();
    const positionsByDex = new Map<string, Map<string, PositionSnapshot>>();
    let pending: V2InvalidateScope | null = null;
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const prune = (map: Map<unknown, number>) => {
        const at = now();
        for (const [key, seen] of map) {
            if (at - seen > DEDUPE_WINDOW_MS) {
                map.delete(key);
            }
        }
    };

    const send = (
        key: string,
        update: Omit<TradeNotificationUpdate, 'type' | 'networkId'>,
    ) => {
        if (disposed) {
            return;
        }
        prune(emittedAt);
        if (emittedAt.has(key)) {
            return;
        }
        emittedAt.set(key, now());
        emit({ type: 1, networkId, ...update });
    };

    const scheduleInvalidate = (coins: string[], account: boolean) => {
        pending ??= { coins: [], account: false };
        for (const coin of coins) {
            if (!pending.coins.includes(coin)) {
                pending.coins.push(coin);
            }
        }
        pending.account ||= account;
        if (invalidateTimer !== null) {
            return;
        }
        invalidateTimer = setTimeout(() => {
            invalidateTimer = null;
            const scope = pending;
            pending = null;
            if (scope && !disposed) {
                invalidate(scope);
            }
        }, INVALIDATE_COALESCE_MS);
    };

    const emitClose = (
        coin: string,
        closingOid: number,
        status: TradeStatus,
    ) => {
        const hints = readHints();
        const entry = hints.entryOidByCoin[coin];
        if (entry === undefined) {
            return;
        }
        send(`close:${closingOid}`, {
            tradeHash: tradeHashFor(hints, entry),
            status,
            coin,
        });
        forgetClosed?.(coin, entry);
    };

    const emitOpen = (fill: FeedFill) => {
        const hints = readHints();
        const entry = String(fill.oid);
        const isLimit = hints.limitOrders[entry] !== undefined;
        if (!isLimit && !isFlat(Number(fill.startPosition))) {
            // an add to a position opened elsewhere: no new trade
            return;
        }
        let snapshot: PositionSnapshot | undefined;
        for (const onDex of positionsByDex.values()) {
            snapshot ??= onDex.get(fill.coin);
        }
        const live =
            snapshot !== undefined && !isFlat(Number(snapshot.szi))
                ? snapshot
                : null;
        const figures: Partial<TradeNotificationUpdate> = {};
        if (live) {
            for (const field of [
                'szi',
                'entryPx',
                'positionValue',
                'unrealizedPnl',
                'liquidationPx',
                'marginUsed',
            ] as const) {
                const value = str(live[field]);
                if (value !== undefined) {
                    figures[field] = value;
                }
            }
        }
        send(`open:${entry}`, {
            tradeHash: tradeHashFor(hints, entry),
            status: 'Open',
            coin: fill.coin,
            ...figures,
            entryPx: figures.entryPx ?? fill.px,
        });
    };

    return {
        fills: (fills) => {
            const hints = readHints();
            prune(filledOids);
            const coins: string[] = [];
            for (const fill of fills) {
                coins.push(fill.coin);
                // this fill decides for its order: an update waiting for it stands down
                filledOids.set(fill.oid, now());
                const waiting = waitingForFill.get(fill.oid);
                if (waiting !== undefined) {
                    clearTimeout(waiting);
                    waitingForFill.delete(fill.oid);
                }
                const liquidated =
                    fill.liquidation !== undefined && fill.liquidation !== null;
                if (!liquidated && fill.dir.startsWith('Open')) {
                    emitOpen(fill);
                    continue;
                }
                if (!liquidated && !fill.dir.startsWith('Close')) {
                    continue;
                }
                if (!leavesFlat(fill)) {
                    continue;
                }
                emitClose(
                    fill.coin,
                    fill.oid,
                    liquidated
                        ? 'Liquidated'
                        : closeStatus(triggerKind(hints, fill.coin, fill.oid)),
                );
            }
            scheduleInvalidate(coins, false);
        },

        orderUpdates: (updates) => {
            const hints = readHints();
            const coins: string[] = [];
            for (const raw of updates) {
                const update = orderUpdateOf(raw);
                if (!update) {
                    continue;
                }
                const { coin, oid, status } = update;
                coins.push(coin);
                const limit = hints.limitOrders[String(oid)];
                if (limit) {
                    if (
                        status === 'open' &&
                        hints.limitEntryOidByCoin[coin] === oid
                    ) {
                        send(`resting:${oid}`, {
                            tradeHash: `hl:${oid}`,
                            status: 'Resting',
                            coin,
                            limitPrice: update.limitPx ?? limit.limitPrice,
                        });
                    } else if (
                        CANCELLED_STATUS.test(status) &&
                        update.unfilled
                    ) {
                        send(`cancelled:${oid}`, {
                            tradeHash: `hl:${oid}`,
                            status: 'Cancelled',
                            coin,
                        });
                    }
                    continue;
                }
                const kind =
                    status === 'filled'
                        ? triggerKind(hints, coin, oid)
                        : undefined;
                // the fill says whether the trigger left the position flat; the update alone cannot
                if (!kind || filledOids.has(oid) || waitingForFill.has(oid)) {
                    continue;
                }
                waitingForFill.set(
                    oid,
                    setTimeout(() => {
                        waitingForFill.delete(oid);
                        emitClose(coin, oid, closeStatus(kind));
                    }, ORDER_UPDATE_GRACE_MS),
                );
            }
            if (coins.length > 0) {
                scheduleInvalidate(coins, false);
            }
        },

        clearinghouse: (state, dex) => {
            positionsByDex.set(dex, positionsOf(state));
            const print = accountFingerprint(state);
            if (print !== null && fingerprints.get(dex) === print) {
                return;
            }
            fingerprints.set(dex, print);
            scheduleInvalidate([], true);
        },

        disconnected: () => {
            // pushes missed meanwhile may have changed the account: the next one invalidates, whatever it says
            fingerprints.clear();
        },

        dispose: () => {
            disposed = true;
            if (invalidateTimer !== null) {
                clearTimeout(invalidateTimer);
                invalidateTimer = null;
            }
            waitingForFill.forEach((timer) => clearTimeout(timer));
            waitingForFill.clear();
        },
    };
}
